import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Wallet,
  TrendingUp,
  Loader2,
  RefreshCw,
  ExternalLink,
  Lock,
  ArrowRight,
  Banknote,
  Shield,
  AlertCircle,
  Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const RewardStatusCard = () => {
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch both reward status and connect status in parallel
      const [rewardResponse, connectResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/referrals/rewards/my-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/payouts/connect/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (rewardResponse.ok) {
        const data = await rewardResponse.json();
        setStatus(data);
      } else {
        const errData = await rewardResponse.json();
        throw new Error(errData.detail || 'Failed to load reward status');
      }
      
      if (connectResponse.ok) {
        const connectData = await connectResponse.json();
        setConnectStatus(connectData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="text-center py-8">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const {
    settled_revenue_cents = 0,
    pending_revenue_cents = 0,
    settled_reward_cents = 0,
    pending_reward_cents = 0,
    paid_reward_cents = 0,
    payable_reward_cents = 0,
    eligibility_threshold_cents = 50000,
    amount_until_threshold_cents = 50000,
    payout_delay_days = 14,
    monthly_cap_cents = 200000,
    monthly_paid_cents = 0,
    meets_threshold = false,
    blocked = false,
    blocked_reason = null,
    stripe_connect_ready = false,
    eligibility = {},
  } = status;

  const rewardProgress = Math.min((settled_reward_cents / eligibility_threshold_cents) * 100, 100);
  const monthlyProgress = (monthly_paid_cents / monthly_cap_cents) * 100;

  // Determine status display
  let statusBadge = null;
  let statusColor = '';
  
  if (blocked) {
    statusBadge = <Badge variant="destructive">Blocked</Badge>;
    statusColor = 'border-red-500/30 bg-red-500/5';
  } else if (payable_reward_cents > 0 && stripe_connect_ready) {
    statusBadge = <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Ready for Payout</Badge>;
    statusColor = 'border-emerald-500/30 bg-emerald-500/5';
  } else if (meets_threshold && !stripe_connect_ready) {
    statusBadge = <Badge variant="outline" className="text-amber-400 border-amber-500/30">Connect Required</Badge>;
    statusColor = 'border-amber-500/30 bg-amber-500/5';
  } else if (pending_reward_cents > 0) {
    statusBadge = <Badge variant="outline">In Progress</Badge>;
  }

  return (
    <div className={`bg-card rounded-xl border ${statusColor || 'border-border'} p-6 space-y-6`} data-testid="reward-status-card">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Banknote className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Reward Earnings</h3>
            <p className="text-xs text-muted-foreground">
              Earn {status.reward_percentage || 10}% of donations you refer
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statusBadge}
          <Button variant="ghost" size="sm" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Blocked Warning */}
      {blocked && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Payouts Blocked</p>
              <p className="text-sm text-muted-foreground mt-1">
                {blocked_reason || 'Your referral payouts have been blocked. Contact support for more information.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-secondary/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-muted-foreground">Total Earned</span>
          </div>
          <p className="text-xl font-bold text-emerald-400">
            {formatCurrency(settled_reward_cents + pending_reward_cents)}
          </p>
        </div>

        <div className="bg-secondary/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-muted-foreground">Settled</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {formatCurrency(settled_reward_cents)}
          </p>
        </div>

        <div className="bg-secondary/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-muted-foreground">Pending ({payout_delay_days}d hold)</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {formatCurrency(pending_reward_cents)}
          </p>
        </div>

        <div className="bg-secondary/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-muted-foreground">Paid Out</span>
          </div>
          <p className="text-xl font-bold text-foreground">
            {formatCurrency(paid_reward_cents)}
          </p>
        </div>
      </div>

      {/* Progress to Threshold */}
      {!meets_threshold && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress to first payout</span>
            <span className="text-foreground font-medium">
              {formatCurrency(settled_reward_cents)} / {formatCurrency(eligibility_threshold_cents)}
            </span>
          </div>
          <Progress value={rewardProgress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {formatCurrency(amount_until_threshold_cents)} more in rewards to unlock payouts
          </p>
        </div>
      )}

      {/* Payable Amount */}
      {payable_reward_cents > 0 && !blocked && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Available for Payout</p>
              <p className="text-2xl font-bold text-emerald-400">
                {formatCurrency(payable_reward_cents)}
              </p>
            </div>
            {stripe_connect_ready ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">Payout Ready</span>
              </div>
            ) : (
              <Link to="/settings?tab=payouts">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Wallet className="w-4 h-4 mr-2" />
                  Connect Payouts
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Stripe Connect CTA */}
      {!stripe_connect_ready && meets_threshold && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-400">Verify & Connect Payouts</p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete your identity verification and connect your bank account to receive payouts.
              </p>
              {connectStatus?.requirements_due?.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <p className="font-medium">Requirements needed:</p>
                  <ul className="list-disc list-inside mt-1">
                    {connectStatus.requirements_due.slice(0, 3).map((req, idx) => (
                      <li key={idx}>{req.replace(/_/g, ' ')}</li>
                    ))}
                    {connectStatus.requirements_due.length > 3 && (
                      <li>And {connectStatus.requirements_due.length - 3} more...</li>
                    )}
                  </ul>
                </div>
              )}
              <Link to="/settings?tab=payouts" className="mt-3 inline-block">
                <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Set Up Payouts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Connect Status Details */}
      {connectStatus && connectStatus.connect_account_id && !connectStatus.can_receive_payouts && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-blue-400">Stripe Connect Status</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  {connectStatus.details_submitted ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  )}
                  <span>Details submitted</span>
                </div>
                <div className="flex items-center gap-1">
                  {connectStatus.payouts_enabled ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  )}
                  <span>Payouts enabled</span>
                </div>
                <div className="flex items-center gap-1">
                  {connectStatus.charges_enabled ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  )}
                  <span>Charges enabled</span>
                </div>
                <div className="flex items-center gap-1">
                  {connectStatus.onboarding_complete ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  )}
                  <span>Onboarding complete</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Cap Progress */}
      {monthly_paid_cents > 0 && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Monthly Payout Limit</span>
            <span className="text-foreground">
              {formatCurrency(monthly_paid_cents)} / {formatCurrency(monthly_cap_cents)}
            </span>
          </div>
          <Progress value={monthlyProgress} className="h-1.5" />
        </div>
      )}

      {/* Eligibility Issues */}
      {eligibility.reasons && eligibility.reasons.length > 0 && !eligibility.eligible && (
        <div className="pt-4 border-t border-border">
          <p className="text-sm font-medium text-muted-foreground mb-2">Payout Requirements</p>
          <ul className="space-y-1">
            {eligibility.reasons.map((reason, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RewardStatusCard;
